import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connectorsApi } from '../services/connectorsApi';
import ConnectorConfigFormModal from '../components/ConnectorConfigFormModal';
import ConnectorTestModal from '../components/ConnectorTestModal';

const PAGE_SIZE = 12;

const emptyFilters = {
  search: '',
  type: '',
};

const ConnectorList = () => {
  const [catalog, setCatalog] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const [draftFilters, setDraftFilters] = useState(emptyFilters);

  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [formConfiguration, setFormConfiguration] = useState(null);

  const [testConfiguration, setTestConfiguration] = useState(null);

  const loadCatalog = useCallback(async () => {
    try {
      const response = await connectorsApi.catalog();
      setCatalog(response.data || []);
    } catch (err) {
      console.error('Erreur lors du chargement du catalogue des connecteurs', err);
    }
  }, []);

  const loadConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        q: filters.search || undefined,
        connector_type: filters.type || undefined,
      };
      const response = await connectorsApi.listConfigured(params);
      setConfigurations(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Erreur lors du chargement des connecteurs configurés', err);
      setError("Impossible de charger les connecteurs configurés.");
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    loadConfigurations();
  }, [loadConfigurations]);

  useEffect(() => {
    setDraftFilters(filters);
  }, [filters]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total]);

  const handleFilterDraftChange = (field) => (event) => {
    const value = event.target.value;
    setDraftFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    setPage(1);
  };

  const clearFilters = () => {
    setDraftFilters(emptyFilters);
    setFilters(emptyFilters);
    setPage(1);
  };

  const openCreateModal = () => {
    setFormConfiguration(null);
    setFormMode('create');
    setFormModalOpen(true);
  };

  const openEditModal = (configuration) => {
    setFormConfiguration(configuration);
    setFormMode('edit');
    setFormModalOpen(true);
  };

  const openDuplicateModal = (configuration) => {
    const copy = {
      ...configuration,
      name: `${configuration.name}-copy`,
    };
    delete copy.id;
    setFormConfiguration(copy);
    setFormMode('duplicate');
    setFormModalOpen(true);
  };

  const handleDelete = async (configuration) => {
    if (!window.confirm(`Supprimer définitivement ${configuration.name} ?`)) {
      return;
    }
    try {
      await connectorsApi.deleteConfigured(configuration.id);
      loadConfigurations();
    } catch (err) {
      console.error('Erreur lors de la suppression', err);
      window.alert('Suppression impossible. Consultez les journaux.');
    }
  };

  const handleSubmitConfiguration = async (payload) => {
    if (formMode === 'edit' && formConfiguration?.id) {
      await connectorsApi.updateConfigured(formConfiguration.id, payload);
    } else {
      await connectorsApi.createConfigured(payload);
    }
    setFormModalOpen(false);
    setFormConfiguration(null);
    loadConfigurations();
  };

  const openTestModal = (configuration) => {
    setTestConfiguration(configuration);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) {
      return;
    }
    setPage(newPage);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-1">Connecteurs configurés</h1>
          <p className="text-sm text-slate-600">
            Gérez les connexions actives, leurs paramètres et les tests.
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center justify-center px-4 py-2 bg-[#E20613] text-white hover:bg-[#c90511] text-sm font-medium"
        >
          Nouveau connecteur configuré
        </button>
      </div>

      <div className="bg-white border border-slate-200 p-4 mb-6 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-800 mb-3">Filtres</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <label className="flex flex-col text-sm text-slate-700">
            Recherche
            <input
              type="text"
              value={draftFilters.search}
              onChange={handleFilterDraftChange('search')}
              className="mt-1 px-3 py-2 border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#E20613]"
              placeholder="Nom ou texte libre"
            />
          </label>

          <label className="flex flex-col text-sm text-slate-700">
            Type
            <select
              value={draftFilters.type}
              onChange={handleFilterDraftChange('type')}
              className="mt-1 px-3 py-2 border border-slate-300 focus:outline-none focus:ring-1 focus:ring-[#E20613]"
            >
              <option value="">Tous</option>
              {catalog.map((typeMeta) => (
                <option key={typeMeta.type} value={typeMeta.type}>
                  {typeMeta.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-4">
          <button
            onClick={applyFilters}
            className="px-4 py-2 bg-[#E20613] text-white hover:bg-[#c90511] text-sm font-medium"
          >
            Appliquer
          </button>
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-sm font-medium border border-slate-300 hover:bg-slate-100"
          >
            Réinitialiser
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 mb-4 text-sm">
          ❌ {error}
        </div>
      )}

      <div className="bg-white border border-slate-200 shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Nom</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">App ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-slate-600 uppercase tracking-wide">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 text-sm text-slate-700">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                    <div className="inline-block h-8 w-8 border-4 border-slate-300 border-t-[#E20613] rounded-full animate-spin" />
                    <p className="mt-2">Chargement des connecteurs configurés…</p>
                  </td>
                </tr>
              ) : configurations.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-slate-500">
                    Aucun connecteur configuré trouvé.
                  </td>
                </tr>
              ) : (
                configurations.map((configuration) => (
                  <tr key={configuration.id}>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <div>{configuration.name}</div>
                      {configuration.description && (
                        <div className="text-xs text-slate-500 mt-1">{configuration.description}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">{configuration.type}</td>
                    <td className="px-4 py-3">{configuration.app_id}</td>
                    <td className="px-4 py-3">
                      {configuration.enabled ? (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-700">
                          Actif
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-slate-100 text-slate-600">
                          Désactivé
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => openEditModal(configuration)}
                          className="px-3 py-1 text-xs font-medium border border-slate-300 hover:bg-slate-100"
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => openDuplicateModal(configuration)}
                          className="px-3 py-1 text-xs font-medium border border-slate-300 hover:bg-slate-100"
                        >
                          Dupliquer
                        </button>
                        <button
                          onClick={() => openTestModal(configuration)}
                          className="px-3 py-1 text-xs font-medium border border-[#004A99] text-[#004A99] hover:bg-red-50"
                        >
                          Tester / exécuter
                        </button>
                        <button
                          onClick={() => handleDelete(configuration)}
                          className="px-3 py-1 text-xs font-medium border border-red-400 text-red-600 hover:bg-red-50"
                        >
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between mt-6 text-sm text-slate-600">
        <div>
          Page {page} / {totalPages}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className="px-3 py-2 border border-slate-300 text-sm disabled:opacity-60"
          >
            Précédent
          </button>
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            className="px-3 py-2 border border-slate-300 text-sm disabled:opacity-60"
          >
            Suivant
          </button>
        </div>
      </div>

      <ConnectorConfigFormModal
        open={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setFormConfiguration(null);
        }}
        onSubmit={handleSubmitConfiguration}
        catalog={catalog}
        initialData={formConfiguration}
        mode={formMode}
      />

      <ConnectorTestModal
        configuration={testConfiguration}
        onClose={() => setTestConfiguration(null)}
      />
    </div>
  );
};

export default ConnectorList;
